/**
 * Iter60.p53 — Leave balance cell (Entitled / Used) chip renderers.
 *
 * Shared by:
 *   • Leave Balances "classic flat list" (per-row × leave-type cells)
 *   • Leave Balances search-first detail card (per-type card)
 *
 * Design (brand parity with Payroll Lines / Attendance / Discipline):
 *   • `EntitledCell` — right-aligned tabular-nums. Zero renders as a
 *     muted `—`; positive values render as a neutral pill so they read
 *     as an "allowance chip".
 *   • `UsedCell` — right-aligned tabular-nums. Colour depends on the
 *     ratio `used / entitled`:
 *       used == 0                  → neutral pill (subdued)
 *       0 < used < entitled        → amber pill (in progress)
 *       used >= entitled AND > 0   → crimson pill (fully consumed / over)
 *
 * Every chip carries an `aria-label` describing the exact value +
 * context so screen-readers announce "Annual leave used: 3 of 15"
 * without visual color cues.
 */
import React from "react";


function _num(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}


function _fmt(v) {
    const n = _num(v);
    if (n === 0) return "0";
    // Keep up to 3 dp when needed (permission-hourly stores fractions),
    // otherwise render the whole integer.
    return Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}


const BASE_PILL = "inline-flex items-center justify-center tabular-nums whitespace-nowrap";
const PILL_STYLE = {
    minWidth: 40,
    padding: "2px 8px",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.1,
    borderRadius: 6,
    letterSpacing: 0.02,
};


export function EntitledCell({ value, leaveTypeLabel, employeeName, testid, tooltip }) {
    const n = _num(value);
    const zero = n === 0;
    const aria = employeeName && leaveTypeLabel
        ? `${leaveTypeLabel} entitled${employeeName ? ` for ${employeeName}` : ""}: ${_fmt(n)}`
        : `Entitled: ${_fmt(n)}`;
    return (
        <td className="px-3 py-3 text-right whitespace-nowrap" data-no-row-click data-testid={testid}>
            <span
                className={`${BASE_PILL} asl-balance-entitled`}
                style={{
                    ...PILL_STYLE,
                    background: zero ? "rgba(0,0,0,0.04)" : "var(--asl-navy-50, #eef1f8)",
                    color: zero ? "var(--asl-text-3)" : "var(--asl-navy)",
                    border: `1px solid ${zero ? "var(--asl-border)" : "rgba(27, 42, 86, 0.14)"}`,
                }}
                aria-label={aria}
                title={tooltip || aria}
            >
                {zero ? "—" : _fmt(n)}
            </span>
        </td>
    );
}


export function UsedCell({ value, entitled, leaveTypeLabel, employeeName, testid, tooltip }) {
    const used = _num(value);
    const ent = _num(entitled);
    let tone = "zero"; // 0 → neutral
    if (used > 0 && used < ent) tone = "partial"; // amber
    else if (used > 0 && used >= ent) tone = "full"; // crimson

    const palette = {
        zero: {
            bg: "rgba(0,0,0,0.04)",
            fg: "var(--asl-text-3)",
            border: "var(--asl-border)",
        },
        partial: {
            bg: "rgba(217, 119, 6, 0.10)", // amber-600 @ 10%
            fg: "#B45309",                 // amber-700 (WCAG AA on the tint)
            border: "rgba(217, 119, 6, 0.22)",
        },
        full: {
            bg: "rgba(168, 50, 74, 0.10)", // crimson @ 10% (matches ModuleIdChip)
            fg: "var(--asl-crimson)",
            border: "rgba(168, 50, 74, 0.20)",
        },
    }[tone];

    const aria = leaveTypeLabel
        ? `${leaveTypeLabel} used${employeeName ? ` for ${employeeName}` : ""}: ${_fmt(used)} of ${_fmt(ent)}`
        : `Used: ${_fmt(used)} of ${_fmt(ent)}`;

    return (
        <td className="px-3 py-3 text-right whitespace-nowrap" data-no-row-click data-testid={testid}>
            <span
                className={`${BASE_PILL} asl-balance-used asl-balance-used--${tone}`}
                style={{
                    ...PILL_STYLE,
                    background: palette.bg,
                    color: palette.fg,
                    border: `1px solid ${palette.border}`,
                }}
                aria-label={aria}
                title={tooltip || aria}
            >
                {used === 0 ? "—" : _fmt(used)}
            </span>
        </td>
    );
}


export default { EntitledCell, UsedCell };
